import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isBefore, isAfter, startOfDay } from 'date-fns';
import { useTranslations } from '@/i18n';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import ExpenseCard from '../cards/ExpenseCard';
import SavedExpenseCard from '../cards/SavedExpenseCard';
import AccommodationExpenseForm, { type AccommodationData } from './AccommodationExpenseForm';
import TransportExpenseForm, { type TransportExpense } from './TransportExpenseForm';
import FuelExpenseForm, { type FuelExpense } from './FuelExpenseForm';
import type { CityFormData } from './CityForm';
import type { TransportationAssistanceType } from '@/hooks/trips/types';
import { useCityExpenses } from '@/hooks/trips/useCityExpenses';
import { useFuelTypes } from '@/hooks/useFuelTypes';
import { Spinner } from '@/components/ui/shadcn-io/spinner';

// Define accommodation type separately to match AccommodationFormData
export interface AccommodationExpense {
  id?: number;
  checkInDate: Date;
  checkOutDate: Date;
  totalAmount: string;
  currency: string;
  needsBookingHelp: boolean;
}

export interface TransportExpenseData {
  id?: number;
  amount: string;
  currency: string;
  assistanceType: TransportationAssistanceType;
  needsBookingSupport: boolean;
  arrivalDate?: Date;
  departureDate?: Date;
}

export interface FuelExpenseData {
  id?: number;
  fuelTypeId: string;
  fuelTypeName: string;
  carModel: string;
  amount: string;
  currency: string;
}

export interface ExpensesFormData {
  accommodation?: AccommodationExpense[];
  transport?: TransportExpenseData[];
  fuel?: FuelExpenseData[];
}

const expensesFormSchema = z.object({
  accommodation: z
    .array(
      z.object({
        id: z.number().optional(),
        checkInDate: z.date(),
        checkOutDate: z.date(),
        totalAmount: z.string(),
        currency: z.string(),
        needsBookingHelp: z.boolean(),
      })
    )
    .optional(),
  transport: z
    .array(
      z.object({
        id: z.number().optional(),
        amount: z.string(),
        currency: z.string(),
        assistanceType: z.enum(['ticket_or_advance', 'rental_car', 'car_with_driver']),
        needsBookingSupport: z.boolean(),
        arrivalDate: z.date().optional(),
        departureDate: z.date().optional(),
      })
    )
    .optional(),
  fuel: z
    .array(
      z.object({
        id: z.number().optional(),
        fuelTypeId: z.string(),
        fuelTypeName: z.string(),
        carModel: z.string(),
        amount: z.string(),
        currency: z.string(),
      })
    )
    .optional(),
});

interface ExpensesFormProps {
  tripId: string;
  cityId?: number;
  city: CityFormData | null;
  tripType?: 'local' | 'international';
  initialData?: ExpensesFormData;
  isBusy?: boolean;
  onFormStateChange?: (hasOpenForm: boolean) => void;
  /** Whether same-day check-in/check-out is allowed (for single-day connected cities) */
  allowSameDayCheckout?: boolean;
}

export default function ExpensesForm({
  tripId,
  cityId,
  city,
  tripType,
  initialData,
  isBusy = false,
  onFormStateChange,
  allowSameDayCheckout = false,
}: ExpensesFormProps) {
  const t = useTranslations('cityForm');
  const tExpenses = useTranslations('expenses');
  const [showAccommodationForm, setShowAccommodationForm] = useState(false);
  const [editingAccommodationIndex, setEditingAccommodationIndex] = useState<number | null>(null);
  const [showTransportForm, setShowTransportForm] = useState(false);
  const [editingTransportIndex, setEditingTransportIndex] = useState<number | null>(null);
  const [showFuelForm, setShowFuelForm] = useState(false);
  const [editingFuelIndex, setEditingFuelIndex] = useState<number | null>(null);
  const isCityReady = cityId !== undefined;
  const {
    createHotel,
    updateHotel,
    deleteHotel,
    createTransportation,
    updateTransportation,
    deleteTransportation,
    createFuel,
    updateFuel,
    deleteFuel,
  } = useCityExpenses(tripId, cityId);
  const { data: fuelTypes = [], isLoading: fuelTypesLoading } = useFuelTypes();
  const [accommodationLoading, setAccommodationLoading] = useState(false);
  const [transportLoading, setTransportLoading] = useState(false);
  const [fuelLoading, setFuelLoading] = useState(false);
  const [accommodationDeletingIndex, setAccommodationDeletingIndex] = useState<number | null>(null);
  const [transportDeletingIndex, setTransportDeletingIndex] = useState<number | null>(null);
  const [fuelDeletingIndex, setFuelDeletingIndex] = useState<number | null>(null);
  const [accommodationError, setAccommodationError] = useState<string | null>(null);
  const isInternationalTrip = tripType === 'international';

  const form = useForm<ExpensesFormData>({
    resolver: zodResolver(expensesFormSchema),
    defaultValues: initialData || {
      accommodation: [],
      transport: [],
      fuel: [],
    },
  });

  useEffect(() => {
    if (initialData) {
      form.reset(initialData);
    }
  }, [initialData, form]);

  const overlayActive = Boolean(isBusy) || accommodationLoading || transportLoading || fuelLoading;
  const fuelTypeOptions = useMemo(() => fuelTypes, [fuelTypes]);
  const transportAssistanceLabels = useMemo<Record<TransportationAssistanceType, string>>(
    () => ({
      ticket_or_advance: tExpenses('transportOptions.ticketOrAdvance'),
      rental_car: tExpenses('transportOptions.rentalCar'),
      car_with_driver: tExpenses('transportOptions.carWithDriver'),
    }),
    [tExpenses]
  );

  // Check if accommodation dates are outside city date range
  const isAccommodationDateOutsideRange = useCallback(
    (checkInDate: Date, checkOutDate: Date): boolean => {
      if (!city?.arrivalDate || !city?.departureDate) {
        return false;
      }

      const cityArrival = startOfDay(city.arrivalDate);
      const cityDeparture = startOfDay(city.departureDate);
      const hotelCheckIn = startOfDay(checkInDate);
      const hotelCheckOut = startOfDay(checkOutDate);

      // Check if hotel check-in is before city arrival or after city departure
      const checkInOutside =
        isBefore(hotelCheckIn, cityArrival) || isAfter(hotelCheckIn, cityDeparture);
      // Check if hotel check-out is before city arrival or after city departure
      const checkOutOutside =
        isBefore(hotelCheckOut, cityArrival) || isAfter(hotelCheckOut, cityDeparture);

      return checkInOutside || checkOutOutside;
    },
    [city?.arrivalDate, city?.departureDate]
  );

  useEffect(() => {
    const hasOpenForm = showAccommodationForm || showTransportForm || showFuelForm;
    onFormStateChange?.(hasOpenForm);
  }, [onFormStateChange, showAccommodationForm, showFuelForm, showTransportForm]);

  const handleAddExpense = (type: 'accommodation' | 'transport' | 'fuel') => {
    if (!isCityReady || overlayActive) {
      return;
    }

    if (type === 'accommodation') {
      setAccommodationError(null);
      setShowAccommodationForm(true);
      setEditingAccommodationIndex(null);
    } else if (type === 'transport') {
      setShowTransportForm(true);
      setEditingTransportIndex(null);
    } else if (type === 'fuel') {
      setShowFuelForm(true);
      setEditingFuelIndex(null);
    }
  };

  const handleSaveAccommodation = async (data: AccommodationData) => {
    if (!isCityReady) {
      return;
    }

    setAccommodationLoading(true);
    setAccommodationError(null);

    const cityArrivalDate = city?.arrivalDate;
    const cityDepartureDate = city?.departureDate;

    // For same-day checkout (single-day connected cities), allow checkout on check-in day
    // Otherwise, checkout must be after check-in
    const isDateOrderInvalid = allowSameDayCheckout
      ? data.checkOutDate < data.checkInDate
      : data.checkOutDate <= data.checkInDate;

    if (isDateOrderInvalid) {
      setAccommodationError(t('hotelDateOrderError'));
      setAccommodationLoading(false);
      return;
    }

    if (cityArrivalDate && data.checkInDate < cityArrivalDate) {
      setAccommodationError(t('hotelCheckInOutsideCity'));
      setAccommodationLoading(false);
      return;
    }

    if (cityDepartureDate && data.checkOutDate > cityDepartureDate) {
      setAccommodationError(t('hotelCheckOutOutsideCity'));
      setAccommodationLoading(false);
      return;
    }

    const currentAccommodations = form.getValues('accommodation') || [];
    const expensePayload: AccommodationExpense = {
      checkInDate: data.checkInDate,
      checkOutDate: data.checkOutDate,
      totalAmount: data.totalAmount,
      currency: data.currency,
      needsBookingHelp: data.needsBookingHelp,
    };

    try {
      let updatedList = [...currentAccommodations];

      if (editingAccommodationIndex !== null) {
        const existing = updatedList[editingAccommodationIndex];
        const saved = existing?.id
          ? await updateHotel(existing.id, { ...expensePayload, id: existing.id })
          : await createHotel(expensePayload);

        updatedList[editingAccommodationIndex] = saved;
      } else {
        const saved = await createHotel(expensePayload);
        updatedList = [...updatedList, saved];
      }

      form.setValue('accommodation', updatedList, { shouldDirty: true });
      setShowAccommodationForm(false);
      setEditingAccommodationIndex(null);
      setAccommodationError(null);
    } catch (error) {
      console.error('Failed to save accommodation:', error);
    } finally {
      setAccommodationLoading(false);
    }
  };

  const handleCancelAccommodation = () => {
    setShowAccommodationForm(false);
    setEditingAccommodationIndex(null);
    setAccommodationError(null);
  };

  const handleSaveTransport = async (data: TransportExpense) => {
    if (!isCityReady) {
      return;
    }

    setTransportLoading(true);

    const currentTransports = form.getValues('transport') || [];
    const assistanceType: TransportationAssistanceType = isInternationalTrip
      ? 'ticket_or_advance'
      : data.assistanceType;

    try {
      let updatedList = [...currentTransports];

      if (editingTransportIndex !== null) {
        const existing = updatedList[editingTransportIndex];
        const saved = existing?.id
          ? await updateTransportation(existing.id, {
              ...existing,
              amount: data.amount,
              currency: data.currency,
              assistanceType,
              needsBookingSupport: data.needsBookingSupport,
              arrivalDate: data.arrivalDate,
              departureDate: data.departureDate,
            })
          : await createTransportation({
              ...data,
              assistanceType,
              id: undefined,
            });

        updatedList[editingTransportIndex] = {
          ...saved,
          assistanceType,
          needsBookingSupport: data.needsBookingSupport,
          arrivalDate: data.arrivalDate,
          departureDate: data.departureDate,
        };
      } else {
        const saved = await createTransportation({ ...data, assistanceType, id: undefined });
        updatedList = [
          ...updatedList,
          {
            ...saved,
            assistanceType,
            needsBookingSupport: data.needsBookingSupport,
            arrivalDate: data.arrivalDate,
            departureDate: data.departureDate,
          },
        ];
      }

      form.setValue('transport', updatedList, { shouldDirty: true });
      setShowTransportForm(false);
      setEditingTransportIndex(null);
    } catch (error) {
      console.error('Failed to save transport:', error);
    } finally {
      setTransportLoading(false);
    }
  };

  const handleCancelTransport = () => {
    setShowTransportForm(false);
    setEditingTransportIndex(null);
  };

  const handleSaveFuel = async (data: FuelExpense) => {
    if (!isCityReady) {
      return;
    }

    setFuelLoading(true);

    const currentFuels = form.getValues('fuel') || [];

    try {
      let updatedList = [...currentFuels];

      if (editingFuelIndex !== null) {
        const existing = updatedList[editingFuelIndex];
        const saved = existing?.id
          ? await updateFuel(existing.id, {
              ...existing,
              fuelTypeId: data.fuelTypeId,
              fuelTypeName: data.fuelTypeName,
              carModel: data.carModel,
              amount: data.amount,
            })
          : await createFuel({ ...data, id: undefined });

        updatedList[editingFuelIndex] = saved;
      } else {
        const saved = await createFuel({ ...data, id: undefined });
        updatedList = [...updatedList, saved];
      }

      form.setValue('fuel', updatedList, { shouldDirty: true });
      setShowFuelForm(false);
      setEditingFuelIndex(null);
    } catch (error) {
      console.error('Failed to save fuel:', error);
    } finally {
      setFuelLoading(false);
    }
  };

  const handleCancelFuel = () => {
    setShowFuelForm(false);
    setEditingFuelIndex(null);
  };

  if (!city || !isCityReady) {
    // Show placeholder when no city is saved yet
    return (
      <div className="relative flex flex-col gap-2 rounded-3xl border border-[#f1f1f1] bg-white px-4 py-5 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)] sm:flex-row sm:items-center sm:px-5">
        <div className="flex flex-1 flex-col gap-2">
          <h3 className="text-[18px] leading-7 font-bold text-[#292929]">{t('expenses')}</h3>
          <p className="text-sm leading-5 text-[#666666]">{t('expensesDescription')}</p>
        </div>
        <img
          src="/assets/calculator.svg"
          alt=""
          className="mx-auto h-[5rem] w-[6rem] sm:static sm:mx-0 sm:translate-y-0"
        />
      </div>
    );
  }

  const hasExpenseRequirements = city.needsAccommodation || city.needsTransport || city.needsFuel;

  if (!hasExpenseRequirements) {
    return (
      <div className="relative flex flex-col gap-2 rounded-3xl border border-[#f1f1f1] bg-white px-4 py-5 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)] sm:flex-row sm:items-center sm:px-5">
        <div className="flex flex-1 flex-col gap-2">
          <h3 className="text-[18px] leading-7 font-bold text-[#292929]">{t('expenses')}</h3>
          <p className="text-sm leading-5 text-[#666666]">{t('expensesDescription')}</p>
        </div>
        <img
          src="/assets/calculator.svg"
          alt=""
          className="mx-auto h-[5rem] w-[6rem] sm:static sm:mx-0 sm:translate-y-0"
        />
      </div>
    );
  }

  // Show expense form when city is saved
  return (
    <Form {...form}>
      <form onSubmit={(event) => event.preventDefault()} className="flex flex-col gap-6">
        <div className="relative flex w-full flex-col gap-4 rounded-3xl bg-white px-4 pt-0 pb-5 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)] sm:px-5">
          {overlayActive && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-3xl bg-white/60 backdrop-blur-sm">
              <Spinner variant="circle" />
            </div>
          )}
          {/* Expenses Header */}
          <div className="flex w-full flex-col gap-1 rounded-t-3xl border-b border-[#f0f0f0] bg-white py-4">
            <p className="text-[18px] leading-7 font-medium text-black">{t('expenses')}</p>
          </div>

          {/* Expense Cards */}
          {city.needsAccommodation && (
            <FormField
              control={form.control}
              name="accommodation"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div>
                      {showAccommodationForm ? (
                        <AccommodationExpenseForm
                          tripId={tripId}
                          onSave={handleSaveAccommodation}
                          onCancel={handleCancelAccommodation}
                          initialData={
                            editingAccommodationIndex !== null
                              ? field.value?.[editingAccommodationIndex]
                              : undefined
                          }
                          isLoading={accommodationLoading}
                          errorMessage={accommodationError}
                          defaultCheckInDate={city?.arrivalDate ?? null}
                          defaultCheckOutDate={city?.departureDate ?? null}
                          allowedCheckInDate={city?.arrivalDate ?? null}
                          allowedCheckOutDate={city?.departureDate ?? null}
                          hasDateIssue={
                            editingAccommodationIndex !== null &&
                            field.value?.[editingAccommodationIndex] !== undefined &&
                            isAccommodationDateOutsideRange(
                              field.value[editingAccommodationIndex].checkInDate,
                              field.value[editingAccommodationIndex].checkOutDate
                            )
                          }
                          allowSameDayCheckout={allowSameDayCheckout}
                        />
                      ) : field.value && field.value.length > 0 ? (
                        <div className="space-y-4">
                          {field.value.map((accommodation, index) => (
                            <SavedExpenseCard
                              key={index}
                              icon="/assets/icons/building.svg"
                              title={t('accommodation')}
                              dateRange={{
                                checkInDate: accommodation.checkInDate,
                                checkOutDate: accommodation.checkOutDate,
                              }}
                              amount={accommodation.totalAmount}
                              currency="₾"
                              hasDateIssue={isAccommodationDateOutsideRange(
                                accommodation.checkInDate,
                                accommodation.checkOutDate
                              )}
                              onEdit={() => {
                                if (overlayActive) {
                                  return;
                                }
                                setEditingAccommodationIndex(index);
                                setShowAccommodationForm(true);
                              }}
                              onDelete={async () => {
                                if (overlayActive) {
                                  return;
                                }
                                const target = field.value?.[index];
                                if (!target) {
                                  return;
                                }

                                if (target.id && isCityReady) {
                                  setAccommodationDeletingIndex(index);
                                  setAccommodationLoading(true);
                                  try {
                                    await deleteHotel(target.id);
                                    const updated = (field.value || []).filter(
                                      (_, i) => i !== index
                                    );
                                    form.setValue('accommodation', updated, { shouldDirty: true });
                                  } catch (error) {
                                    console.error('Failed to delete accommodation:', error);
                                  } finally {
                                    setAccommodationDeletingIndex(null);
                                    setAccommodationLoading(false);
                                  }
                                } else {
                                  const updated = (field.value || []).filter((_, i) => i !== index);
                                  form.setValue('accommodation', updated, { shouldDirty: true });
                                }
                              }}
                              isDeleting={accommodationDeletingIndex === index}
                            />
                          ))}
                        </div>
                      ) : (
                        <ExpenseCard
                          icon="/assets/icons/building.svg"
                          title={t('accommodation')}
                          onAdd={() => handleAddExpense('accommodation')}
                          expenses={[]}
                        />
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {city.needsTransport && (
            <FormField
              control={form.control}
              name="transport"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div>
                      {showTransportForm ? (
                        <TransportExpenseForm
                          onSave={handleSaveTransport}
                          onCancel={handleCancelTransport}
                          isInternationalTrip={isInternationalTrip}
                          initialData={
                            editingTransportIndex !== null
                              ? field.value?.[editingTransportIndex]
                              : undefined
                          }
                          isLoading={transportLoading}
                          cityDates={
                            city
                              ? {
                                  arrivalDate: city.arrivalDate,
                                  departureDate: city.departureDate,
                                }
                              : undefined
                          }
                        />
                      ) : field.value && field.value.length > 0 ? (
                        <div className="space-y-4">
                          {field.value.map((transport, index) => {
                            const assistanceKey = transport.assistanceType ?? 'ticket_or_advance';
                            const assistanceLabel = transportAssistanceLabels[assistanceKey];
                            const normalizedAmount = transport.amount?.trim() ?? '';
                            const hasAmount = normalizedAmount.length > 0;

                            return (
                              <SavedExpenseCard
                                key={index}
                                icon="/assets/icons/plane.svg"
                                title={assistanceLabel}
                                subtitle={t('transport')}
                                dateRange={
                                  transport.arrivalDate && transport.departureDate
                                    ? {
                                        checkInDate: transport.arrivalDate,
                                        checkOutDate: transport.departureDate,
                                      }
                                    : undefined
                                }
                                amount={normalizedAmount}
                                currency={hasAmount ? '₾' : undefined}
                                formattedAmount={
                                  hasAmount ? undefined : tExpenses('transportNoAmount')
                                }
                                onEdit={() => {
                                  if (overlayActive) {
                                    return;
                                  }
                                  setEditingTransportIndex(index);
                                  setShowTransportForm(true);
                                }}
                                onDelete={async () => {
                                  if (overlayActive) {
                                    return;
                                  }
                                  const target = field.value?.[index];
                                  if (!target) {
                                    return;
                                  }

                                  if (target.id && isCityReady) {
                                    setTransportDeletingIndex(index);
                                    setTransportLoading(true);
                                    try {
                                      await deleteTransportation(target.id);
                                      const updated = (field.value || []).filter(
                                        (_, i) => i !== index
                                      );
                                      form.setValue('transport', updated, { shouldDirty: true });
                                    } catch (error) {
                                      console.error('Failed to delete transport:', error);
                                    } finally {
                                      setTransportDeletingIndex(null);
                                      setTransportLoading(false);
                                    }
                                  } else {
                                    const updated = (field.value || []).filter(
                                      (_, i) => i !== index
                                    );
                                    form.setValue('transport', updated, { shouldDirty: true });
                                  }
                                }}
                                isDeleting={transportDeletingIndex === index}
                              />
                            );
                          })}
                        </div>
                      ) : (
                        <ExpenseCard
                          icon="/assets/icons/plane.svg"
                          title={t('transport')}
                          onAdd={() => handleAddExpense('transport')}
                          expenses={[]}
                        />
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {city.needsFuel && (
            <FormField
              control={form.control}
              name="fuel"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div>
                      {showFuelForm ? (
                        <FuelExpenseForm
                          onSave={handleSaveFuel}
                          onCancel={handleCancelFuel}
                          initialData={
                            editingFuelIndex !== null ? field.value?.[editingFuelIndex] : undefined
                          }
                          isLoading={fuelLoading}
                          fuelTypes={fuelTypeOptions}
                          fuelTypesLoading={fuelTypesLoading}
                        />
                      ) : field.value && field.value.length > 0 ? (
                        <div className="space-y-4">
                          {field.value.map((fuel, index) => (
                            <SavedExpenseCard
                              key={index}
                              icon="/assets/icons/gas-station.svg"
                              title={fuel.fuelTypeName || t('fuel')}
                              subtitle={fuel.carModel}
                              amount={fuel.amount}
                              formattedAmount={`${fuel.amount} ${tExpenses('liters')}`}
                              onEdit={() => {
                                if (overlayActive) {
                                  return;
                                }
                                setEditingFuelIndex(index);
                                setShowFuelForm(true);
                              }}
                              onDelete={async () => {
                                if (overlayActive) {
                                  return;
                                }
                                const target = field.value?.[index];
                                if (!target) {
                                  return;
                                }

                                if (target.id && isCityReady) {
                                  setFuelDeletingIndex(index);
                                  setFuelLoading(true);
                                  try {
                                    await deleteFuel(target.id);
                                    const updated = (field.value || []).filter(
                                      (_, i) => i !== index
                                    );
                                    form.setValue('fuel', updated, { shouldDirty: true });
                                  } catch (error) {
                                    console.error('Failed to delete fuel expense:', error);
                                  } finally {
                                    setFuelDeletingIndex(null);
                                    setFuelLoading(false);
                                  }
                                } else {
                                  const updated = (field.value || []).filter((_, i) => i !== index);
                                  form.setValue('fuel', updated, { shouldDirty: true });
                                }
                              }}
                              isDeleting={fuelDeletingIndex === index}
                            />
                          ))}
                        </div>
                      ) : (
                        <ExpenseCard
                          icon="/assets/icons/gas-station.svg"
                          title={t('fuel')}
                          onAdd={() => handleAddExpense('fuel')}
                          expenses={[]}
                        />
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </form>
    </Form>
  );
}
